import React, { FC } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FormChildQuestionDto, FormQuestionDto } from "@green0meter/api-client";
import styled from "@emotion/styled";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useTranslation } from "react-i18next";
import { InfoOutlined } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";

import { Button, FormControl, Stack, Link } from "@components/base";
import { RichTextBox as MuiRichTextBox } from "@components/common";
import { useUserStore } from "@store/user";
import { useGetCurrentCompanyInfo, useGetAIAssistance } from "@api";
import { useDraftReturn } from "@hooks/useDraftField";

interface RichTextBox {
  parentQuestionCode?: string;
  question: FormQuestionDto | FormChildQuestionDto;
  disabled?: boolean;
  draftField?: useDraftReturn | null;
}

const StyledRichTextContainer = styled.div`
  .ql-editor {
    min-height: 120px;
  }
`;

const RichTextBox: FC<RichTextBox> = ({
  question,
  disabled,
  draftField,
  parentQuestionCode,
}) => {
  const { control } = useFormContext();
  const { t, i18n } = useTranslation();
  const { user } = useUserStore();
  const { currentCompanyInfo } = useGetCurrentCompanyInfo(user);
  const aiAssistance = useGetAIAssistance();

  const handleAI = (
    currentValue: string,
    onChange: (value: string) => void,
  ): void => {
    const body = {
      question: question.name,
      description: question.description || "",
      companyName: currentCompanyInfo?.name || "",
      language: i18n.resolvedLanguage || "en",
      aiPrompt: "",
      userInput: currentValue || "",
    };

    aiAssistance.mutate(body, {
      onSuccess: (result: string) => {
        onChange(result);
      },
      onError: () => {
        enqueueSnackbar(t("common.somethingWrong"), { variant: "error" });
      },
    });
  };

  return (
    <FormControl fullWidth disabled={disabled}>
      <Controller
        name={
          parentQuestionCode
            ? `${parentQuestionCode}-${question.code}`
            : question.code
        }
        control={control}
        render={({ field }) => (
          <>
            <StyledRichTextContainer>
              <MuiRichTextBox
                {...field}
                value={field.value ?? ""}
                onChange={field.onChange}
                disabled={disabled}
                onFocus={draftField?.handleFocus}
                onBlur={draftField?.handleBlur}
                placeholder={
                  (question as FormQuestionDto).placeholder ||
                  "Type your answer here..."
                }
              />
            </StyledRichTextContainer>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button
                onClick={() => handleAI(field.value, field.onChange)}
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                size="small"
                disabled={disabled || aiAssistance.isPending}
                isLoading={aiAssistance.isPending}
              >
                {t("common.aiAssistant")}
              </Button>
              <Link
                href="https://intercom.help/green0meter/en/articles/10091507-ai-assistant"
                target="_blank"
              >
                <InfoOutlined color="disabled" />
              </Link>
            </Stack>
          </>
        )}
      />
    </FormControl>
  );
};

export default RichTextBox;
